import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { SetInitialStateFacade } from '../../../../../common/models';
import { Observable, startWith, combineLatest } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';
import {
  BookingHistoryLoad,
  BookingHistoryInitialState,
  BookingHistoryPreviewConfirmationPDF,
  BookingHistoryDownloadConfirmationPDF,
} from './booking-history.actions';
import { BookingHistoryState } from './booking-history.state';
import {
  AllocationViewSlotBriefModel,
  AllocationViewSlotBriefUliInfoModel,
  BookingHistoryCriteria,
  BookingHistoryItem,
} from '../models';
import { UliBriefModel } from '../uli/models';
import { UliBriefFacade } from '../uli/uli-brief/uli-brief.facade';
import { AllocationViewSlotBriefFacade } from '../slot';

@Injectable({
  providedIn: 'root',
})
export class BookingHistoryFacade implements SetInitialStateFacade {
  historyArray$: Observable<BookingHistoryItem[]> = this.store.select(BookingHistoryState.getItems);
  loaded$: Observable<boolean> = this.store.select(BookingHistoryState.getLoaded);
  pdfPreviewParams$: Observable<BookingHistoryCriteria> = combineLatest([
    this.uliBriefFacade.uli$.pipe(startWith(null)),
    this.slotBriefFacade.slot$.pipe(startWith(null)),
  ]).pipe(
    debounceTime(0),
    map(([uli, slot]: [UliBriefModel, AllocationViewSlotBriefModel]) => {
      const slotLeadULI: AllocationViewSlotBriefUliInfoModel | undefined = slot?.uliInfos.find(
        (uliInfo: AllocationViewSlotBriefUliInfoModel) => uliInfo.leadUli,
      );

      return {
        uli: uli?.uli ?? slotLeadULI?.uli,
        compartmentId: uli?.compartmentId ?? slotLeadULI?.compartmentId,
        uliType: uli?.type ?? slotLeadULI?.type,
      };
    }),
    filter(Boolean),
  );

  constructor(
    private store: Store,
    private uliBriefFacade: UliBriefFacade,
    private slotBriefFacade: AllocationViewSlotBriefFacade,
  ) {}

  loadAll(): void {
    this.getBookingHistoryCriteria().subscribe((criteria: BookingHistoryCriteria) =>
      this.updateBookingHistory(criteria),
    );
  }

  updateBookingHistory(criteria: BookingHistoryCriteria): void {
    this.store.dispatch(new BookingHistoryLoad(criteria));
  }

  previewConfirmationPDF(params: BookingHistoryCriteria): void {
    this.store.dispatch(new BookingHistoryPreviewConfirmationPDF(params));
  }

  downloadConfirmationPDF(params: BookingHistoryCriteria): void {
    this.store.dispatch(new BookingHistoryDownloadConfirmationPDF(params));
  }

  setInitialState(): void {
    this.store.dispatch(new BookingHistoryInitialState());
  }

  private getBookingHistoryCriteria(): Observable<BookingHistoryCriteria> {
    return combineLatest([
      this.uliBriefFacade.uli$.pipe(startWith(null)),
      this.slotBriefFacade.slot$.pipe(startWith(null)),
    ]).pipe(
      map(([uli, slot]: [UliBriefModel, AllocationViewSlotBriefModel]) => {
        if (uli) {
          return {
            compartmentId: uli.compartmentId,
            uli: uli.uli,
            uliType: uli.type,
          };
        }

        if (slot) {
          return {
            compartmentId: slot.compartmentId,
            uli: slot.uliInfos[0].uli,
            uliType: slot.uliInfos[0].type,
          };
        }

        return null;
      }),
      filter(Boolean),
      take(1),
    );
  }
}
